import Select, { StylesConfig, components, ValueContainerProps, OptionProps, ClearIndicatorProps, DropdownIndicatorProps } from 'react-select';
import { Check, X } from 'lucide-react';

interface DropdownOption {
    value: any;
    label: string;
}

interface CommonDropdownProps {
    value: any;
    onChange: (value: any) => void;
    options: DropdownOption[];
    placeholder?: string;
    isSearchable?: boolean;
    isMulti?: boolean;
    isClearable?: boolean;
    className?: string;
    menuPlacement?: 'auto' | 'top' | 'bottom';
    size?: 'sm' | 'md';
    disabled?: boolean;
    loading?: boolean;
}

const ClearIndicator = (props: ClearIndicatorProps<DropdownOption, boolean>) => {
    return (
        <components.ClearIndicator {...props}>
            <X size={13} strokeWidth={2} />
        </components.ClearIndicator>
    );
};

const DropdownIndicator = (props: DropdownIndicatorProps<DropdownOption, boolean>) => {
    if (props.hasValue) return null;
    return <components.DropdownIndicator {...props} />;
};

const Option = (props: OptionProps<DropdownOption, boolean>) => {
    const { isSelected, children } = props;
    return (
        <components.Option {...props}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                width: '100%',
                minWidth: 0
            }}>
                <span style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {children}
                </span>
                {isSelected && (
                    <Check size={14} color="#0e7490" strokeWidth={2.5} style={{ flexShrink: 0 }} />
                )}
            </div>
        </components.Option>
    );
};

const ValueContainer = ({ children, ...props }: ValueContainerProps<DropdownOption, true>) => {
    const { getValue, hasValue } = props;
    const nbValues = getValue().length;
    if (!hasValue || !props.isMulti || nbValues <= 10) {
        return (
            <components.ValueContainer {...props}>
                {children}
            </components.ValueContainer>
        );
    }

    // children[0] is the array of MultiValue components
    // children[1] is the placeholder/input
    const [values, input] = children as any;
    const shownValues = values.slice(0, 10);
    const hiddenCount = nbValues - 10;

    return (
        <components.ValueContainer {...props}>
            {shownValues}
            <div style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                background: '#f1f5f9',
                padding: '2px 6px',
                borderRadius: '4px',
                marginLeft: '4px',
                whiteSpace: 'nowrap'
            }}>
                +{hiddenCount} more
            </div>
            {input}
        </components.ValueContainer>
    );
};

const CommonDropdown: React.FC<CommonDropdownProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Select...',
    isSearchable = false,
    isMulti = false,
    isClearable = true,
    className = '',
    menuPlacement = 'auto',
    size = 'sm',
    disabled = false,
    loading = false
}) => {
    const controlHeight = size === 'sm' ? 30 : 38;
    const fontSize = size === 'sm' ? 12 : 13;

    const customStyles: StylesConfig<DropdownOption, boolean> = {
        control: (provided, state) => ({
            ...provided,
            minHeight: `${controlHeight}px`,
            height: isMulti ? 'auto' : `${controlHeight}px`,
            fontSize: `${fontSize}px`,
            border: `1px solid ${state.isFocused ? '#83cee4' : '#cbd5e1'}`,
            borderRadius: '6px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(131, 206, 228, 0.18)' : 'none',
            backgroundColor: disabled ? '#f8fafc' : '#fff',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                borderColor: state.isFocused ? '#83cee4' : '#94a3b8'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: `${controlHeight}px`,
            height: isMulti ? 'auto' : `${controlHeight}px`,
            padding: isMulti ? '3px 10px' : '0 12px',
            display: isMulti ? 'flex' : provided.display,
            flexWrap: isMulti ? 'wrap' : provided.flexWrap,
            gap: isMulti ? '4px' : undefined
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#1e293b',
            fontWeight: 500,
            margin: 0
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#94a3b8',
            fontWeight: 400,
            margin: 0
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            color: '#1e293b'
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: isMulti ? 'auto' : `${controlHeight}px`,
            alignSelf: 'stretch'
        }),
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: state.isFocused ? '#475569' : '#94a3b8',
            padding: size === 'sm' ? '4px 6px' : '6px 8px',
            transition: 'color 0.15s ease, transform 0.15s ease',
            transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            '&:hover': {
                color: '#475569'
            }
        }),
        clearIndicator: (provided) => ({
            ...provided,
            color: '#cbd5e1',
            padding: size === 'sm' ? '2px' : '4px',
            marginRight: size === 'sm' ? '2px' : '4px',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'color 0.15s ease, background-color 0.15s ease',
            '&:hover': {
                color: '#64748b',
                backgroundColor: '#f1f5f9'
            }
        }),
        loadingIndicator: (provided) => ({
            ...provided,
            color: '#83cee4'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#e2f3f9',
            borderRadius: '4px',
            margin: '2px 0',
            border: '1px solid rgba(131, 206, 228, 0.6)',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#011926',
            fontSize: `${Math.max(fontSize - 1, 11)}px`,
            fontWeight: 600,
            padding: '2px 6px'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#0e7490',
            cursor: 'pointer',
            padding: '0 4px',
            transition: 'background-color 0.15s ease, color 0.15s ease',
            '&:hover': {
                backgroundColor: '#83cee4',
                color: '#011926'
            }
        }),
        menu: (provided) => ({
            ...provided,
            marginTop: '4px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12), 0 2px 6px rgba(15, 23, 42, 0.06)',
            zIndex: 9999,
            overflow: 'hidden'
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '6px',
            borderRadius: '8px'
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: `${fontSize}px`,
            padding: size === 'sm' ? '7px 10px' : '9px 12px',
            borderRadius: '6px',
            fontWeight: state.isSelected ? 600 : 500,
            backgroundColor: state.isSelected
                ? '#e2f3f9'
                : state.isFocused
                    ? '#f1f5f9'
                    : 'transparent',
            color: state.isSelected ? '#011926' : '#1e293b',
            cursor: 'pointer',
            transition: 'background-color 0.1s ease, color 0.1s ease',
            '&:active': {
                backgroundColor: '#bfe4f0'
            }
        }),
        noOptionsMessage: (provided) => ({
            ...provided,
            fontSize: `${fontSize}px`,
            color: '#94a3b8',
            padding: '10px 12px'
        }),
        groupHeading: (provided) => ({
            ...provided,
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#94a3b8',
            padding: '8px 10px 4px'
        })
    };

    return (
        <Select
            value={
                isMulti
                    ? options.filter((option) => (value || []).includes(option.value))
                    : options.find((option) => option.value === value)
            }
            onChange={(selected: any) => {
                if (isMulti) {
                    const selectedOptions = (selected || []) as DropdownOption[];
                    onChange(selectedOptions.map((opt) => opt.value));
                } else {
                    const selectedOption = selected as DropdownOption;
                    onChange(selectedOption ? selectedOption.value : null);
                }
            }}
            options={options}
            isMulti={isMulti as any}
            components={{ ValueContainer, Option, ClearIndicator, DropdownIndicator }}
            styles={customStyles}
            isSearchable={isSearchable}
            isClearable={isClearable}
            closeMenuOnSelect={!isMulti}
            className={className}
            menuPlacement={menuPlacement}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            placeholder={placeholder}
            isLoading={loading}
            isDisabled={disabled}
        />
    );
};

export default CommonDropdown;
